import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Avatar } from '../components/Avatar'
import {
  fetchPraesentationFolienHandy,
  fetchPraesentationStand,
  fetchPraesentationVerlaufHandy,
  sendPraesentationBefehl,
  sendPraesentationFrage,
  transcribeIntro,
  type PraesentationBefehl,
  type PraesentationFolie,
  type PraesentationNachricht,
  type PraesentationStand,
} from '../lib/api'
import { streamChatEvents } from '../lib/sse'

/** Wie oft das Handy nachsieht, was der Bildschirm tut. */
const POLL_MS = 1000
/** Längste Aufnahme einer Frage. */
const AUFNAHME_MAX_MS = 45_000

/**
 * Die Steuerung der Präsentation am Handy (Modus „Präsentation“ auf
 * `/buehne/go/<token>`). Oben steht, welche Folie der Bildschirm zeigt und
 * ob K4ia spricht; darunter der Sprechtext zum Mitlesen, dann die Knöpfe:
 *
 * - **Weiter** / **Zurück** — Folie für Folie, K4ia spricht jede.
 * - **Pause** / **Fortsetzen** — mitten im Satz.
 * - An der ersten Quartalsfolie: **Quartale zeigen** (alle Quartalsfolien,
 *   einzeln) oder **Kurzfassung** (K4ia spricht sie und macht hinter dem
 *   Bereich weiter).
 * - Nach der letzten Folie: **Fragen aus dem Publikum** — getippt oder ins
 *   Mikrofon gesprochen; K4ia antwortet nur aus den Folien, der Bildschirm
 *   liest vor.
 * - **Beenden** bringt den Bildschirm auf Anfang.
 *
 * `token` ist das Bühnen-Token; `sttToken` das Intro-Token für die
 * Spracherkennung (`/api/v1/intro/stt/<token>`), die es nur dort gibt.
 */
export function PraesentationRemotePage({ token, sttToken }: { token: string; sttToken: string }) {
  const [stand, setStand] = useState<PraesentationStand | null>(null)
  const [folien, setFolien] = useState<PraesentationFolie[] | null>(null)
  const [quartale, setQuartale] = useState<{ von: number; bis: number } | null>(null)
  const [kurzfassung, setKurzfassung] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const ladeFolien = () =>
    fetchPraesentationFolienHandy(token)
      .then((f) => {
        setFolien(f.folien)
        setKurzfassung(f.kurzfassung)
        setQuartale(
          f.quartaleVon !== null && f.quartaleBis !== null
            ? { von: f.quartaleVon, bis: f.quartaleBis }
            : null,
        )
      })
      .catch(() => setFehler('Die Folien ließen sich nicht laden.'))

  useEffect(() => {
    void ladeFolien()
  }, [token])

  useEffect(() => {
    let aktiv = true
    const takt = () =>
      void fetchPraesentationStand()
        .then((s) => {
          if (aktiv) setStand(s)
        })
        .catch(() => undefined)
    takt()
    const id = setInterval(takt, POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [])

  async function befehl(b: PraesentationBefehl) {
    if (busy) return
    setFehler(null)
    setBusy(true)
    try {
      setStand(await sendPraesentationBefehl(token, b))
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Der Befehl kam nicht an.')
    } finally {
      setBusy(false)
    }
  }

  if (!stand || !folien) {
    return (
      <Rahmen avatar={fehler ? 'error' : 'thinking'}>
        <p className="text-xl">{fehler ?? 'Einen Moment …'}</p>
      </Rahmen>
    )
  }

  if (folien.length === 0) {
    return (
      <Rahmen avatar="idle">
        <p className="text-xl">Keine Folien hinterlegt.</p>
        <p className="text-text-muted">
          Im Admin unter „Präsentation“ die PDF und das Skript laden.
        </p>
      </Rahmen>
    )
  }

  const letzte = folien[folien.length - 1]!.nr
  const folie = folien.find((f) => f.nr === stand.folie)
  const verbunden = stand.verbunden
  const spricht = stand.bildschirm.spricht && !stand.bildschirm.pausiert
  const pausiert = stand.bildschirm.pausiert

  if (stand.phase === 'bereit') {
    return (
      <Rahmen avatar="idle">
        <p className="text-xl">Präsentation</p>
        <p className="text-text-muted">
          {verbunden
            ? `${folien.length} Folien. K4ia spricht jede Folie, du klickst weiter.`
            : 'Der Bildschirm ist nicht verbunden. Öffne dort die Bühnen-Adresse aus dem Admin und klicke einmal hinein.'}
        </p>
        <button
          onClick={() => void befehl({ art: 'start' })}
          disabled={busy}
          className="btn-primary w-full rounded-2xl px-8 py-5 text-xl font-semibold"
        >
          Präsentation starten
        </button>
        {fehler && <Fehler text={fehler} />}
      </Rahmen>
    )
  }

  const kopf = (
    <header className="flex flex-col gap-1" data-testid="praesentation-kopf">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold uppercase tracking-wide text-text-muted">
          Folie {stand.folie} von {letzte}
        </p>
        <p className="text-sm text-text-muted" aria-live="polite">
          {!verbunden
            ? 'Bildschirm nicht verbunden'
            : pausiert
              ? 'Pause'
              : spricht
                ? 'K4ia spricht …'
                : stand.phase === 'fragen'
                  ? 'Fragerunde'
                  : 'wartet'}
        </p>
      </div>
      <h2 className="text-xl font-semibold">{folie?.titel || `Folie ${stand.folie}`}</h2>
    </header>
  )

  if (stand.phase === 'fragen') {
    return (
      <main className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col gap-4 bg-bg p-4">
        {kopf}
        <Fragerunde token={token} sttToken={sttToken} />
        <div className="mt-auto flex gap-2">
          <button
            onClick={() => void befehl({ art: 'zurueck' })}
            disabled={busy}
            className="flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold"
          >
            Letzte Folie
          </button>
          <Beenden onClick={() => void befehl({ art: 'ende' })} disabled={busy} />
        </div>
        {fehler && <Fehler text={fehler} />}
      </main>
    )
  }

  const text = stand.kurzfassung ? kurzfassung : (folie?.text ?? '')

  return (
    <main
      className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col gap-4 bg-bg p-4"
      data-testid="praesentation-handy"
      data-folie={stand.folie}
    >
      {kopf}

      <section
        className="max-h-[32dvh] overflow-y-auto whitespace-pre-line rounded-2xl border border-border bg-bg-elevated p-4 text-base leading-relaxed"
        aria-label={stand.kurzfassung ? 'Kurzfassung' : 'Sprechtext dieser Folie'}
      >
        {stand.kurzfassung && (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Kurzfassung
          </p>
        )}
        {text || <span className="text-text-muted">Diese Folie wird nur gezeigt.</span>}
      </section>

      {stand.entscheidung ? (
        <section
          className="flex flex-col gap-2 rounded-2xl border border-accent/40 bg-accent/10 p-4"
          data-testid="praesentation-entscheidung"
        >
          <p className="font-semibold">
            Quartalsfolien {quartale?.von}–{quartale?.bis}: einzeln zeigen oder abkürzen?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => void befehl({ art: 'quartale' })}
              disabled={busy}
              className="flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold"
            >
              Quartale zeigen
            </button>
            <button
              onClick={() => void befehl({ art: 'kurzfassung' })}
              disabled={busy || !kurzfassung}
              className="btn-primary flex-1 rounded-xl px-4 py-3 font-semibold"
            >
              Kurzfassung
            </button>
          </div>
        </section>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => void befehl({ art: pausiert ? 'fortsetzen' : 'pause' })}
            disabled={busy || (!spricht && !pausiert)}
            className="flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold disabled:opacity-50"
          >
            {pausiert ? 'Fortsetzen' : 'Pause'}
          </button>
          <button
            onClick={() => void befehl({ art: stand.automatik ? 'automatik_aus' : 'automatik_an' })}
            disabled={busy}
            aria-pressed={stand.automatik}
            data-testid="praesentation-automatik"
            title="K4ia klickt nach jeder Folie selbst weiter — bis zur Entscheidung an den Quartalsfolien und vor der Fragerunde. Pause, Zurück und Springen schalten sie aus."
            className={`flex-1 rounded-xl border px-4 py-3 font-semibold disabled:opacity-50 ${
              stand.automatik
                ? 'border-brand bg-brand/15 text-text'
                : 'border-border bg-bg-elevated text-text-muted'
            }`}
          >
            {stand.automatik ? 'Automatik an' : 'Automatik aus'}
          </button>
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => void befehl({ art: 'zurueck' })}
          disabled={busy || (stand.folie <= 1 && !stand.kurzfassung)}
          className="flex-1 rounded-2xl border border-border bg-bg-elevated px-4 py-5 text-lg font-semibold disabled:opacity-50"
        >
          ‹ Zurück
        </button>
        {stand.entscheidung ? (
          // An der Entscheidung gibt es kein „Weiter“: Die Wahl oben ist der nächste Schritt.
          <p className="flex flex-[2] items-center justify-center text-sm text-text-muted">
            Oben wählen
          </p>
        ) : stand.folie >= letzte ? (
          <button
            onClick={() => void befehl({ art: 'fragen' })}
            disabled={busy}
            className="btn-primary flex-[2] rounded-2xl px-4 py-5 text-lg font-semibold"
          >
            Fragen beantworten
          </button>
        ) : (
          <button
            onClick={() => void befehl({ art: 'weiter' })}
            disabled={busy}
            className="btn-primary flex-[2] rounded-2xl px-4 py-5 text-lg font-semibold"
          >
            Weiter ›
          </button>
        )}
      </div>

      <div className="mt-auto flex items-center gap-2">
        <label className="flex flex-1 items-center gap-2 text-sm text-text-muted">
          <span className="shrink-0">Springen:</span>
          <select
            value={stand.folie}
            onChange={(e) => void befehl({ art: 'springen', folie: Number(e.target.value) })}
            disabled={busy}
            className="w-full rounded-xl border border-border bg-bg-elevated px-2 py-2 text-text"
            aria-label="Zu Folie springen"
          >
            {folien.map((f) => (
              <option key={f.nr} value={f.nr}>
                {f.nr}
                {f.titel ? ` – ${f.titel}` : ''}
              </option>
            ))}
          </select>
        </label>
        <Beenden onClick={() => void befehl({ art: 'ende' })} disabled={busy} />
      </div>
      {fehler && <Fehler text={fehler} />}
    </main>
  )
}

function Beenden({ onClick, disabled }: { onClick: () => void; disabled: boolean }) {
  return (
    <button
      onClick={() => {
        if (window.confirm('Präsentation beenden? Der Bildschirm geht auf Anfang.')) onClick()
      }}
      disabled={disabled}
      className="shrink-0 rounded-xl border border-border bg-bg-elevated px-4 py-3 font-semibold"
    >
      Beenden
    </button>
  )
}

/**
 * Fragen aus dem Publikum: tippen oder sprechen. Die Antwort läuft hier als
 * Text mit, der Bildschirm zeigt und spricht sie. Was im Verlauf steht,
 * kommt vom Worker (fertige Antworten), was gerade entsteht, aus dem Stream.
 */
function Fragerunde({ token, sttToken }: { token: string; sttToken: string }) {
  const [verlauf, setVerlauf] = useState<PraesentationNachricht[]>([])
  const [eingabe, setEingabe] = useState('')
  const [laufend, setLaufend] = useState<{ frage: string; antwort: string } | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const aufnahme = useAufnahme()
  const ende = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let aktiv = true
    const lesen = () =>
      void fetchPraesentationVerlaufHandy(token)
        .then((r) => {
          if (aktiv) setVerlauf(r.nachrichten)
        })
        .catch(() => undefined)
    lesen()
    const id = setInterval(lesen, 2000)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [token])

  useEffect(() => {
    ende.current?.scrollIntoView?.({ block: 'end' })
  }, [verlauf.length, laufend?.antwort])

  async function fragen(frage: string) {
    const text = frage.trim()
    if (!text || laufend) return
    setFehler(null)
    setEingabe('')
    setLaufend({ frage: text, antwort: '' })
    try {
      const res = await sendPraesentationFrage(token, text)
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string }
        throw new Error(body.error ?? body.message ?? `HTTP ${res.status}`)
      }
      for await (const event of streamChatEvents(res)) {
        if (event.type === 'token') {
          setLaufend((l) => (l ? { ...l, antwort: l.antwort + event.delta } : l))
        }
      }
      setVerlauf((await fetchPraesentationVerlaufHandy(token)).nachrichten)
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Frage kam nicht an.')
    } finally {
      setLaufend(null)
    }
  }

  function senden(e: FormEvent) {
    e.preventDefault()
    void fragen(eingabe)
  }

  async function mikrofon() {
    setFehler(null)
    if (aufnahme.status === 'hoert') {
      const blob = await aufnahme.stoppen()
      if (!blob) return
      aufnahme.setStatus('versteht')
      try {
        const text = await transcribeIntro(sttToken, blob)
        if (!text) {
          setFehler('Ich habe nichts verstanden — tipp noch einmal und sprich etwas lauter.')
          return
        }
        await fragen(text)
      } catch (e) {
        setFehler(
          e instanceof Error ? e.message : 'Die Spracherkennung ist gerade nicht erreichbar.',
        )
      } finally {
        aufnahme.setStatus('bereit')
      }
      return
    }
    try {
      await aufnahme.starten()
    } catch {
      setFehler('Ohne Mikrofon geht es nicht — erlaube es bitte in der Browser-Nachfrage.')
    }
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-3" data-testid="praesentation-fragen">
      <p className="text-sm text-text-muted">
        Fragen aus dem Publikum — K4ia antwortet nur aus den Folien; alles Verbindliche verweist sie
        an die Gründer.
      </p>
      <div className="flex max-h-[40dvh] flex-col gap-2 overflow-y-auto">
        {verlauf.length === 0 && !laufend && <p className="text-text-muted">Noch keine Frage.</p>}
        {verlauf.map((n) => (
          <Blase key={n.id} rolle={n.role}>
            {n.content}
          </Blase>
        ))}
        {laufend && (
          <>
            <Blase rolle="user">{laufend.frage}</Blase>
            <Blase rolle="assistant">{laufend.antwort || '…'}</Blase>
          </>
        )}
        <div ref={ende} />
      </div>
      <form onSubmit={senden} className="flex gap-2">
        <input
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          placeholder="Frage eingeben …"
          disabled={laufend !== null}
          className="min-w-0 flex-1 rounded-xl border border-border bg-bg-elevated px-4 py-3"
          aria-label="Frage"
        />
        <button
          type="button"
          onClick={() => void mikrofon()}
          disabled={laufend !== null || aufnahme.status === 'versteht'}
          aria-pressed={aufnahme.status === 'hoert'}
          aria-label={aufnahme.status === 'hoert' ? 'Fertig gesprochen' : 'Frage sprechen'}
          className={`shrink-0 rounded-xl px-4 py-3 font-semibold ${
            aufnahme.status === 'hoert'
              ? 'bg-status-active text-white'
              : 'border border-border bg-bg-elevated'
          }`}
        >
          {aufnahme.status === 'hoert' ? 'Fertig' : aufnahme.status === 'versteht' ? '…' : '🎤'}
        </button>
        <button
          type="submit"
          disabled={laufend !== null || !eingabe.trim()}
          className="btn-primary shrink-0 rounded-xl px-4 py-3 font-semibold"
        >
          Senden
        </button>
      </form>
      {fehler && <Fehler text={fehler} />}
    </section>
  )
}

function Blase({ rolle, children }: { rolle: 'user' | 'assistant'; children: ReactNode }) {
  return (
    <p
      className={`max-w-[90%] whitespace-pre-line rounded-2xl px-4 py-2 ${
        rolle === 'user'
          ? 'self-end bg-accent/15 text-text'
          : 'self-start border border-border bg-bg-elevated'
      }`}
    >
      {children}
    </p>
  )
}

type AufnahmeStatus = 'bereit' | 'hoert' | 'versteht'

/** Nimmt zwischen `starten` und `stoppen` auf — ausdrücklich angetippt, wie am Intro-Handy. */
function useAufnahme() {
  const [status, setStatus] = useState<AufnahmeStatus>('bereit')
  const recorder = useRef<MediaRecorder | null>(null)
  const teile = useRef<Blob[]>([])
  const auszeit = useRef<ReturnType<typeof setTimeout> | null>(null)
  const fertig = useRef<((blob: Blob | null) => void) | null>(null)

  async function starten() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const typ = ['audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t))
    const r = new MediaRecorder(stream, typ ? { mimeType: typ } : undefined)
    teile.current = []
    r.ondataavailable = (e) => {
      if (e.data.size > 0) teile.current.push(e.data)
    }
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop())
      const blob = teile.current.length ? new Blob(teile.current, { type: r.mimeType }) : null
      fertig.current?.(blob)
      fertig.current = null
    }
    recorder.current = r
    r.start()
    setStatus('hoert')
    auszeit.current = setTimeout(() => {
      void stoppen().then(() => setStatus('bereit'))
    }, AUFNAHME_MAX_MS)
  }

  function stoppen(): Promise<Blob | null> {
    if (auszeit.current) clearTimeout(auszeit.current)
    const r = recorder.current
    recorder.current = null
    if (!r || r.state === 'inactive') {
      setStatus('bereit')
      return Promise.resolve(null)
    }
    return new Promise((resolve) => {
      fertig.current = resolve
      r.stop()
    })
  }

  return { status, setStatus, starten, stoppen }
}

function Fehler({ text }: { text: string }) {
  return (
    <p className="text-center text-sm text-status-error" role="alert">
      {text}
    </p>
  )
}

function Rahmen({
  avatar,
  children,
}: {
  avatar: 'idle' | 'thinking' | 'speaking' | 'error'
  children: ReactNode
}) {
  return (
    <main className="flex min-h-[calc(100dvh_-_var(--buehne-leiste,0px))] flex-col items-center justify-center gap-6 bg-bg p-8 text-center">
      <Avatar state={avatar} size={140} showLabel={false} />
      <div className="flex w-full max-w-sm flex-col items-center gap-4">{children}</div>
    </main>
  )
}
